import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      // Fetch user details from API
      try {
        const userData = await api.getUserProfile();
        setUser({ ...userData, token });
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        // If API call fails, use stored user data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            setUser({ ...JSON.parse(storedUser), token });
          } catch (e) {
            setUser({ token });
          }
        } else {
          setUser({ token });
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in and load user data
    const token = localStorage.getItem('token');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [loadUser]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser({ ...userData, token });
    } else {
      setUser({ token });
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('guestCart'); // Clear guest cart on logout
    setUser(null);
    window.location.href = '/'; // Redirect to home
  };

  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: isAuthenticated(),
    loadUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

